import { computed } from 'mobx';
import type { Patch } from 'mobx-keystone';
import {
  Model,
  model,
  modelAction,
  objectToMapTransform,
  onPatches,
  prop,
} from 'mobx-keystone';

import type { InteractiveQuestionAnswerPayload } from '@genially/contracts';
import { isPostScoreableInteractiveQuestionAnswerRequestBody } from '@genially/contracts';
import type {
  GameState,
  GameStatePatchWithVersion,
  InteractiveQuestionState,
  PlayerData,
  PlayerSendInteractiveQuestionAnswerMessage,
  PlayerSendScoreableInteractiveQuestionAnswerMessage,
  PlayerUserAnswer,
} from '@genially/p2p-lib';

@model('p2p-client/PlayerUserAnswer')
export class PlayerUserAnswerModel extends Model({
  player: prop<PlayerData>(),
  answer: prop<InteractiveQuestionAnswerPayload>(),
  isCorrect: prop<boolean | undefined>(undefined),
}) {
  @computed
  get asJS(): PlayerUserAnswer {
    if (this.isCorrect !== undefined) {
      return {
        player: this.player,
        answer: this.answer,
        isCorrect: this.isCorrect,
      } as PlayerUserAnswer;
    }

    return {
      player: this.player,
      answer: this.answer,
    } as PlayerUserAnswer;
  }
}

export const createPlayerUserAnswerModelFromType = (
  playerUserAnswer: PlayerUserAnswer,
): PlayerUserAnswerModel => {
  if ('isCorrect' in playerUserAnswer) {
    return new PlayerUserAnswerModel({
      player: playerUserAnswer.player,
      answer: playerUserAnswer.answer,
      isCorrect: playerUserAnswer.isCorrect,
    });
  }

  return new PlayerUserAnswerModel({
    player: playerUserAnswer.player,
    answer: playerUserAnswer.answer,
  });
};

@model('p2p-client/InteractiveQuestionStateModel')
export class InteractiveQuestionStateModel extends Model({
  playerUserAnswers: prop<PlayerUserAnswerModel[]>(() => []),
  isResolved: prop<boolean>(false),
}) {}

export const createInteractiveQuestionStateModelFromType = (
  interactiveQuestionState: InteractiveQuestionState,
): InteractiveQuestionStateModel => {
  return new InteractiveQuestionStateModel({
    playerUserAnswers: interactiveQuestionState.playerUserAnswers.map(
      createPlayerUserAnswerModelFromType,
    ),
    isResolved: interactiveQuestionState.isResolved,
  });
};

@model('p2p-client/GameStateModel')
export class GameStateModel extends Model({
  isTrackedInLogRocket: prop<boolean>(false).withSetter(),
  isStarted: prop<boolean>(false),
  startedAt: prop<number | undefined>(undefined),
  isFinished: prop<boolean>(false),
  finishedAt: prop<number | undefined>(undefined),
  isSessionBlocked: prop<boolean>(false),
  currentSlide: prop<string>(''),
  version: prop<number>(0),
  interactiveQuestionAnswers: prop<Record<string, InteractiveQuestionStateModel>>(
    () => ({}),
  ).withTransform(objectToMapTransform()),
}) {
  private disposers: (() => void)[] = [];

  private lastPatches: GameStatePatchWithVersion[] = [];

  @modelAction
  setIsStarted(isStarted: boolean) {
    this.isStarted = isStarted;
    this.startedAt = isStarted ? Date.now() : undefined;
  }

  @modelAction
  setIsFinished(isFinished: boolean) {
    this.isFinished = isFinished;
    this.finishedAt = isFinished ? Date.now() : undefined;
  }

  @modelAction
  setIsSessionBlocked(isSessionBlocked: boolean) {
    this.isSessionBlocked = isSessionBlocked;
  }

  @modelAction
  setCurrentSlide(currentSlide: string) {
    this.currentSlide = currentSlide;
  }

  @modelAction
  setVersion(version: number) {
    this.version = version;
  }

  @modelAction
  setInteractiveQuestionAnswers(
    interactiveQuestionAnswers: Record<string, InteractiveQuestionState>,
  ) {
    this.interactiveQuestionAnswers = new Map(
      Object.entries(interactiveQuestionAnswers).map(([interactiveQuestionId, state]) => [
        interactiveQuestionId,
        createInteractiveQuestionStateModelFromType(state),
      ]),
    );
  }

  @computed
  get playerCount() {
    return Object.keys(this.interactiveQuestionAnswers).length;
  }

  @computed
  get asJS(): GameState {
    return {
      isTrackedInLogRocket: this.isTrackedInLogRocket,
      isStarted: this.isStarted,
      isFinished: this.isFinished,
      startedAt: this.startedAt,
      finishedAt: this.finishedAt,
      isSessionBlocked: this.isSessionBlocked,
      currentSlide: this.currentSlide,
      version: this.version,
      interactiveQuestionAnswers: Object.fromEntries(
        Array.from(this.interactiveQuestionAnswers.entries()).map(
          ([interactiveQuestionId, state]) => [
            interactiveQuestionId,
            {
              playerUserAnswers: state.playerUserAnswers.map(
                playerUserAnswer => playerUserAnswer.asJS,
              ),
              isResolved: state.isResolved,
            },
          ],
        ),
      ),
    };
  }

  get sessionDuration() {
    if (!this.startedAt || !this.finishedAt) {
      return undefined;
    }

    return Math.floor((this.finishedAt - this.startedAt) / 1000);
  }

  get hasPendingPatches() {
    return this.lastPatches.length > 0;
  }

  @modelAction
  getAndResetPatches() {
    const patches = this.lastPatches;
    this.lastPatches = [];
    return patches;
  }

  @modelAction
  private onPatch = (patches: Patch[]) => {
    // In case that the patch is the version, we ignore it
    if (patches.some(patch => patch.path.some(path => path === 'version'))) {
      return;
    }

    this.version += 1;

    this.lastPatches.push({
      patches,
      version: this.version,
    });
  };

  @modelAction
  reset() {
    this.setIsFinished(false);
    this.interactiveQuestionAnswers.clear();
  }

  dispose() {
    this.disposers.forEach(disposer => disposer());
  }

  onInit() {
    this.disposers.push(onPatches(this, this.onPatch.bind(this)));
  }

  @modelAction
  toggleOpenAnswerVisibility(
    interactiveQuestionId: string,
    username: string,
  ): PlayerUserAnswerModel | undefined {
    const interactiveQuestionAnswer =
      this.interactiveQuestionAnswers.get(interactiveQuestionId);

    if (!interactiveQuestionAnswer) {
      this.interactiveQuestionAnswers.set(
        interactiveQuestionId,
        createInteractiveQuestionStateModelFromType({
          playerUserAnswers: [],
          isResolved: false,
        }),
      );

      return undefined;
    }

    const interactiveQuestion = interactiveQuestionAnswer.playerUserAnswers.find(
      answer =>
        answer.player.username === username &&
        answer.answer.interactiveQuestionId === interactiveQuestionId,
    );

    if (!interactiveQuestion) {
      return undefined;
    }

    interactiveQuestion.answer.isHidden = !interactiveQuestion.answer.isHidden;

    return interactiveQuestion;
  }

  @modelAction
  addInteractiveQuestionAnswer(
    player: PlayerData,
    payload:
      | { answer: PlayerSendInteractiveQuestionAnswerMessage['payload'] }
      | PlayerSendScoreableInteractiveQuestionAnswerMessage['payload'],
  ) {
    const { interactiveQuestionId } = payload.answer;

    let interactiveQuestionAnswer =
      this.interactiveQuestionAnswers.get(interactiveQuestionId);

    if (!interactiveQuestionAnswer) {
      interactiveQuestionAnswer = createInteractiveQuestionStateModelFromType({
        playerUserAnswers: [],
        isResolved: false,
      });

      this.interactiveQuestionAnswers.set(
        interactiveQuestionId,
        interactiveQuestionAnswer,
      );
    }

    if (
      isPostScoreableInteractiveQuestionAnswerRequestBody(payload.answer) &&
      'isCorrect' in payload
    ) {
      interactiveQuestionAnswer.playerUserAnswers.push(
        createPlayerUserAnswerModelFromType({
          player,
          answer: payload.answer,
          isCorrect: payload.isCorrect,
        } as PlayerUserAnswer),
      );
    } else {
      interactiveQuestionAnswer.playerUserAnswers.push(
        createPlayerUserAnswerModelFromType({
          player,
          answer: payload.answer,
        } as PlayerUserAnswer),
      );
    }
  }

  @modelAction
  resolveInteractiveQuestion(interactiveQuestionId: string) {
    const interactiveQuestionAnswer =
      this.interactiveQuestionAnswers.get(interactiveQuestionId);

    if (!interactiveQuestionAnswer) {
      this.interactiveQuestionAnswers.set(
        interactiveQuestionId,
        createInteractiveQuestionStateModelFromType({
          playerUserAnswers: [],
          isResolved: true,
        }),
      );
    } else {
      interactiveQuestionAnswer.isResolved = true;
    }
  }

  getInteractiveQuestionAnswer(
    interactiveQuestionId: string,
  ): InteractiveQuestionStateModel | undefined {
    return this.interactiveQuestionAnswers.get(interactiveQuestionId);
  }
}

export const createGameStateModelFromType = (gameState: GameState): GameStateModel => {
  return new GameStateModel({
    isStarted: gameState.isStarted,
    isFinished: gameState.isFinished,
    startedAt: gameState.startedAt,
    finishedAt: gameState.finishedAt,
    isSessionBlocked: gameState.isSessionBlocked,
    currentSlide: gameState.currentSlide,
    version: gameState.version,
    interactiveQuestionAnswers: new Map(
      Object.entries(gameState.interactiveQuestionAnswers).map(
        ([interactiveQuestionId, state]) => [
          interactiveQuestionId,
          createInteractiveQuestionStateModelFromType(state),
        ],
      ),
    ),
  });
};
